import { createContext, useContext, useState, type FocusEvent, type KeyboardEvent, type PointerEvent, type ReactElement, type ReactNode } from "react";
import { AT_REST, activatesOn, type Interaction } from "./state.ts";

/**
 * How a control learns what the pointer and the keyboard are doing to it (brief rule 10), so its state
 * picks a frame from the state tables rather than from CSS pseudo-classes, which cannot swap a
 * `border-image` class. A hover never reveals anything a click or a tap does not (brief rule 24): it only
 * changes a frame.
 *
 * `ShowState` forces an interaction on every control inside it, for the `/ui` gallery to show hover,
 * pressed and focus without a pointer or a keyboard; a real interaction still adds to it.
 */

/**
 * An interaction the gallery can force.
 */
export type ShownState = keyof Interaction;

const Shown = createContext<Interaction>(AT_REST);

/**
 * Shows every control inside it in the given states, as if hovered, pressed or focused, for the `/ui`
 * gallery.
 *
 * @param props The states to show and the controls.
 * @returns The provider.
 */
export function ShowState({ states, children }: { readonly states: readonly ShownState[]; readonly children: ReactNode }): ReactElement {
  const shown: Interaction = { hover: states.includes("hover"), pressed: states.includes("pressed"), focus: states.includes("focus") };
  return <Shown.Provider value={shown}>{children}</Shown.Provider>;
}

/**
 * The handlers a control spreads on the element the pointer and the keyboard reach.
 */
export interface InteractionHandlers {
  readonly onPointerEnter: (event: PointerEvent<HTMLElement>) => void;
  readonly onPointerLeave: (event: PointerEvent<HTMLElement>) => void;
  readonly onPointerDown: (event: PointerEvent<HTMLElement>) => void;
  readonly onPointerUp: (event: PointerEvent<HTMLElement>) => void;
  readonly onPointerCancel: (event: PointerEvent<HTMLElement>) => void;
  readonly onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
  readonly onKeyUp: (event: KeyboardEvent<HTMLElement>) => void;
  readonly onFocus: (event: FocusEvent<HTMLElement>) => void;
  readonly onBlur: (event: FocusEvent<HTMLElement>) => void;
}

/**
 * Tracks hover, press and focus on a control, adding any state `ShowState` forces. A disabled control is
 * always at rest. A key that activates the focused control (`activatesOn`: Space or Enter on a button,
 * Space on a checkbox, Enter on a link) holds it pressed, as the pointer does, and is stopped at the
 * control on its way down and up: neither the input router nor Phaser's keyboard manager, which prevents a
 * key the game scene captures and so would cancel the browser's activation, ever sees it (CLIENT.md 7.3).
 * The first press is never prevented, so the browser still clicks or toggles the control. An auto-repeat of
 * a held Enter is prevented, so holding it clicks the control once: a control that opens a menu or a dialog
 * never has the repeat land on, and choose, the item that takes focus as it opens. Space needs no such
 * guard, since the browser activates on its release.
 *
 * @param options `disabled`, and `focus`: `visible` counts only keyboard focus (`:focus-visible`), as a
 *   button shows it; `any` counts every focus, as a text field shows it.
 * @returns The interaction, and the handlers to spread on the control.
 */
export function useInteraction(options: { readonly disabled: boolean; readonly focus: "visible" | "any" }): { readonly state: Interaction; readonly handlers: InteractionHandlers } {
  const shown = useContext(Shown);
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);
  const [focus, setFocus] = useState(false);

  /**
   * Whether a key activates the control it went down or up on.
   *
   * @param event The key event.
   * @returns True for an activation key of that control.
   */
  const activates = (event: KeyboardEvent<HTMLElement>): boolean => activatesOn({ tag: event.currentTarget.tagName, type: event.currentTarget.getAttribute("type") ?? "" }, event.key);
  const handlers: InteractionHandlers = {
    /**
     * The pointer came over the control.
     */
    onPointerEnter: () => setHover(true),
    /**
     * The pointer left, which also ends a press.
     */
    onPointerLeave: () => {
      setHover(false);
      setPressed(false);
    },
    /**
     * The primary button went down on the control.
     *
     * @param event The pointer event.
     */
    onPointerDown: (event) => {
      if (event.button === 0) {
        setPressed(true);
      }
    },
    /**
     * The pointer went up.
     */
    onPointerUp: () => setPressed(false),
    /**
     * The browser took the pointer away.
     */
    onPointerCancel: () => {
      setHover(false);
      setPressed(false);
    },
    /**
     * A key that activates the focused control went down: it holds the control pressed and goes no
     * further than the control, and an auto-repeat of Enter activates nothing.
     *
     * @param event The key event.
     */
    onKeyDown: (event) => {
      if (activates(event)) {
        event.stopPropagation();
        if (event.repeat && event.key === "Enter") {
          event.preventDefault();
        }
        setPressed(true);
      }
    },
    /**
     * A key that activates the focused control went up: it ends the keyboard press and goes no further
     * than the control, since the browser clicks a button and toggles a checkbox as Space comes up.
     *
     * @param event The key event.
     */
    onKeyUp: (event) => {
      if (activates(event)) {
        event.stopPropagation();
        setPressed(false);
      }
    },
    /**
     * The control took focus; a button shows it only when the browser would show a focus ring.
     *
     * @param event The focus event.
     */
    onFocus: (event) => setFocus(options.focus === "any" || event.currentTarget.matches(":focus-visible")),
    /**
     * The control lost focus, which also ends a keyboard press.
     */
    onBlur: () => {
      setFocus(false);
      setPressed(false);
    },
  };
  if (options.disabled) {
    return { state: AT_REST, handlers };
  }
  return { state: { hover: hover || shown.hover, pressed: pressed || shown.pressed, focus: focus || shown.focus }, handlers };
}
